import { useMemo, type Dispatch, type SetStateAction } from 'react'
import styled from 'styled-components'

import { ArrowReplyIcon, MinusIcon } from '@blade/ui-kit'
import { CommentItem } from '@root/entities/comment/ui'

import { COMMENT_ITEM_AVATAR_SIZE } from './consts'

import type { CommentId } from '../../model/comment-id'
import type { Comment } from '@root/entities/comment'
import type { CommentsModel } from '@root/features/comment/atoms'

type CommentItemRepliesProps = {
  comment: Comment.Comment

  hasMoreReplies: boolean
  depth: number | undefined
  collapseId: CommentId | undefined
  highlightId: CommentId | undefined

  setCollapse: Dispatch<SetStateAction<CommentId | undefined>>
  setHighlight: Dispatch<SetStateAction<CommentId | undefined>>
  setDepth: Dispatch<SetStateAction<number | undefined>>
  model: CommentsModel
}

export const CommentItemReplies = ({
  comment,
  depth,
  collapseId,
  highlightId,
  hasMoreReplies,

  setCollapse,
  setHighlight,
  setDepth,
  model,
}: CommentItemRepliesProps) => {
  const sortedReplies = useMemo(
    () =>
      comment.replies.sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [comment.replies],
  )

  const repliesDepthTotal = sortedReplies.length

  return sortedReplies.map((reply, index: number) => {
    const skipReply = depth !== undefined && index > depth
    const replyCollapseHidden = depth !== index && reply.id === collapseId

    const isHighlighted = highlightId === comment.id

    if (skipReply) {
      return false
    }

    return (
      <Row key={reply.id}>
        {index < repliesDepthTotal - 1 || hasMoreReplies ? (
          <Highlight
            onMouseEnter={() => {
              setHighlight(comment.id)
              setCollapse(reply.id)
            }}
            onMouseLeave={() => {
              setHighlight(undefined)
              setCollapse(undefined)
            }}
            onClick={() => setDepth(index)}
          >
            {replyCollapseHidden && <ButtonCollapse />}
            <Line $highlight={isHighlighted} />
            <CornerArrowIcon size={11} $highlight={isHighlighted} />
          </Highlight>
        ) : (
          <Blank />
        )}

        <CommentsTree>
          <CommentItem comment={reply} model={model} />
        </CommentsTree>
      </Row>
    )
  })
}

const Row = styled.div`
  display: flex;
  align-items: center;

  gap: 10px;
`

const Line = styled.div<{ $highlight: boolean }>`
  width: ${COMMENT_ITEM_AVATAR_SIZE}px;

  background: ${(props) =>
    props.$highlight
      ? LineBackgroundUrl('white')
      : LineBackgroundUrl('rgb(96, 96, 96)')};

  position: relative;
  cursor: pointer;

  z-index: -1;
  background-position: 10px;

  transition: background 0.1s ease-in-out;
`

const LineBackgroundUrl = (color: string): string => {
  const svg = `<svg height="17" width="17" xmlns="http://www.w3.org/2000/svg"><line x1="0" y1="0" x2="0" y2="20" stroke-width="2" stroke="${color}" />`

  return `url('data:image/svg+xml,${svg}</svg>')`
}

const Highlight = styled.div`
  position: relative;
  display: grid;
  cursor: pointer;

  margin-top: 5px;
  height: 100%;
  width: ${COMMENT_ITEM_AVATAR_SIZE}px;
`

const ButtonCollapse = styled(MinusIcon)`
  background: ${(props) => props.theme.color.white[100]};
  color: ${(props) => props.theme.color.black[100]};

  cursor: pointer;
  position: absolute;
  display: grid;
  justify-content: center;
  align-items: center;

  width: 17px;
  height: 17px;
  left: 2px;
  top: 50%;
  padding: 3px;
  z-index: 1;
  border-radius: 50%;

  &:hover {
    opacity: 1;
  }
`

const CornerArrowIcon = styled(ArrowReplyIcon)<{
  $highlight: boolean
}>`
  color: ${(props) =>
    props.$highlight ? 'var(--background-12)' : 'var(--background-8)'};

  position: absolute;

  left: ${COMMENT_ITEM_AVATAR_SIZE / 2}px;
  bottom: -12px;
  z-index: -2;

  transition: color 0.1s ease-in-out;
`

const Blank = styled.aside`
  width: ${COMMENT_ITEM_AVATAR_SIZE}px;
`

const CommentsTree = styled.section`
  width: 100%;
`
